import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  Tooltip,
  Collapse,
  Radio,
  RadioGroup,
  FormControl,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WineBarIcon from '@mui/icons-material/WineBar';
import InventoryIcon from '@mui/icons-material/Inventory';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import config from '../config';
import Logger from '../utils/Logger';
import { getValidToken } from '../services/tokenRefreshService';
import { DeliveryTierBuilder, DeliveryTier } from './DeliveryTierBuilder';
import { SHOW_SLA } from '../config/features';

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Uber delivery verification settings type.
 * Matches Uber Direct API requirements.
 */
interface UberVerificationSettings {
  signature?: {
    enabled: boolean;
    collect_signer_name?: boolean;
    collect_signer_relationship?: boolean;
  };
  picture?: boolean;
  pincode?: {
    enabled: boolean;
  };
}

/**
 * Alert settings for SLA monitoring.
 * Controls when manager alerts are triggered for orders in various states.
 */
interface AlertSettings {
  alertsEnabled: boolean;
  pickingSlaMinutes: number;  // Time before alert if order waiting to start picking (PLACED state)
  packingSlaMinutes: number;  // Time before alert if order in PICKING state too long
  pickupSlaMinutes: number;   // Time before alert if order PACKED waiting for courier
  // Note: slaWebhookUrl is managed on the Integrations page
}

export function StoreSettingsEdit() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [storeName, setStoreName] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Delivery tier state
  const [useDefaultTiers, setUseDefaultTiers] = useState(true);
  const [storeTiers, setStoreTiers] = useState<DeliveryTier[]>([]);
  const [defaultTiers, setDefaultTiers] = useState<DeliveryTier[]>([]);

  // Uber delivery settings
  const [pickupNotes, setPickupNotes] = useState<string>('');
  const [uberDropoffVerification, setUberDropoffVerification] = useState<UberVerificationSettings | null>(null);
  const [uberReturnVerification, setUberReturnVerification] = useState<UberVerificationSettings | null>(null);

  // Uber defaults (for display)
  const [defaultUberDropoff, setDefaultUberDropoff] = useState<UberVerificationSettings | null>(null);
  const [defaultUberReturn, setDefaultUberReturn] = useState<UberVerificationSettings | null>(null);

  // Use default checkboxes for Uber settings
  const [useDefaultUberDropoff, setUseDefaultUberDropoff] = useState(true);
  const [useDefaultUberReturn, setUseDefaultUberReturn] = useState(true);

  // Picking flow settings
  const [enablePickingFlow, setEnablePickingFlow] = useState(false);

  // Alert settings
  const [alertSettings, setAlertSettings] = useState<AlertSettings>({
    alertsEnabled: true,
    pickingSlaMinutes: 10,
    packingSlaMinutes: 15,
    pickupSlaMinutes: 30,
  });
  const [defaultAlertSettings, setDefaultAlertSettings] = useState<AlertSettings>({
    alertsEnabled: true,
    pickingSlaMinutes: 10,
    packingSlaMinutes: 15,
    pickupSlaMinutes: 30,
  });
  const [useDefaultAlertSettings, setUseDefaultAlertSettings] = useState(true);

  // Collapsible section expanded states
  const [pricingSectionExpanded, setPricingSectionExpanded] = useState(false);
  const [hoursSectionExpanded, setHoursSectionExpanded] = useState(false);
  const [cutoffsSectionExpanded, setCutoffsSectionExpanded] = useState(false);
  const [uberSectionExpanded, setUberSectionExpanded] = useState(false);
  const [operationsSectionExpanded, setOperationsSectionExpanded] = useState(false);

  // Working hours state
  const [workingHours, setWorkingHours] = useState<Record<number, { openTime: string; closeTime: string }>>({});
  const [defaultHours, setDefaultHours] = useState<Record<number, { openTime: string; closeTime: string }>>({});
  const [useDefaultHours, setUseDefaultHours] = useState<Record<number, boolean>>({});

  // Liquor cutoff state
  const [earlyCutoffs, setEarlyCutoffs] = useState<Record<number, string>>({});
  const [defaultCutoffs, setDefaultCutoffs] = useState<Record<number, string>>({});
  const [useDefaultCutoffs, setUseDefaultCutoffs] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchStoreSettings();
  }, [storeId]);

  const fetchStoreSettings = async () => {
    const token = await getValidToken();

    // Fetch store details
    const storeRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/settings`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await storeRes.json();

    // Handle API errors
    if (!storeRes.ok || !data.store) {
      Logger.error('Failed to fetch store settings:', data);
      alert('Failed to load store settings. Check console for details.');
      return;
    }

    setStoreName(data.store.name);
    setIsActive(data.store.isActive ?? true);

    // Set delivery tier state
    setDefaultTiers(data.defaults.deliveryTiers || []);
    if (data.store.deliveryTiers) {
      setStoreTiers(data.store.deliveryTiers);
      setUseDefaultTiers(false);
    } else {
      setStoreTiers(data.defaults.deliveryTiers || []);
      setUseDefaultTiers(true);
    }

    // Load Uber delivery settings
    setPickupNotes(data.store.pickupNotes || '');

    // Set defaults for display
    setDefaultUberDropoff(data.defaults.uberDropoffVerification || null);
    setDefaultUberReturn(data.defaults.uberReturnVerification || null);

    // Set store values and checkboxes for Uber dropoff
    if (data.store.uberDropoffVerification !== null) {
      setUberDropoffVerification(data.store.uberDropoffVerification);
      setUseDefaultUberDropoff(false);
      setUberSectionExpanded(true); // Expand if custom settings exist
    } else {
      setUberDropoffVerification(data.defaults.uberDropoffVerification || null);
      setUseDefaultUberDropoff(true);
    }

    // Set store values and checkboxes for Uber return
    if (data.store.uberReturnVerification !== null) {
      setUberReturnVerification(data.store.uberReturnVerification);
      setUseDefaultUberReturn(false);
      setUberSectionExpanded(true); // Expand if custom settings exist
    } else {
      setUberReturnVerification(data.defaults.uberReturnVerification || null);
      setUseDefaultUberReturn(true);
    }

    // Load picking flow setting
    setEnablePickingFlow(data.store.enablePickingFlow ?? false);

    // Load default alert settings
    if (data.defaults.alertSettings) {
      setDefaultAlertSettings(data.defaults.alertSettings);
    }

    // Load store alert settings
    if (data.store.alertSettings !== null) {
      setAlertSettings(data.store.alertSettings);
      setUseDefaultAlertSettings(false);
      setOperationsSectionExpanded(true); // Expand if custom settings exist
    } else {
      setAlertSettings(data.defaults.alertSettings || {
        alertsEnabled: true,
        pickingSlaMinutes: 10,
        packingSlaMinutes: 15,
        pickupSlaMinutes: 30,
      });
      setUseDefaultAlertSettings(true);
    }

    // Expand operations section if picking flow is enabled
    if (data.store.enablePickingFlow) {
      setOperationsSectionExpanded(true);
    }

    // Fetch working hours
    const hoursRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/working-hours`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const hoursData = await hoursRes.json();

    // Convert hours to object
    const storeHoursObj: Record<number, { openTime: string; closeTime: string }> = {};
    hoursData.forEach((h: any) => {
      storeHoursObj[h.dayOfWeek] = {
        openTime: h.openTime.substring(0, 5),
        closeTime: h.closeTime.substring(0, 5),
      };
    });

    // Set default hours from data.defaults
    const defaultHoursObj: Record<number, { openTime: string; closeTime: string }> = {};
    if (data.defaults.workingHours) {
      data.defaults.workingHours.forEach((h: any) => {
        defaultHoursObj[h.dayOfWeek] = {
          openTime: h.openTime.substring(0, 5),
          closeTime: h.closeTime.substring(0, 5),
        };
      });
    }

    // Set use default checkboxes
    const useDefaultHoursObj: Record<number, boolean> = {};
    const finalHours: Record<number, { openTime: string; closeTime: string }> = {};
    for (let day = 0; day < 7; day++) {
      if (storeHoursObj[day]) {
        useDefaultHoursObj[day] = false;
        finalHours[day] = storeHoursObj[day];
      } else {
        useDefaultHoursObj[day] = true;
        finalHours[day] = defaultHoursObj[day] || { openTime: '08:00', closeTime: '22:00' };
      }
    }

    setDefaultHours(defaultHoursObj);
    setWorkingHours(finalHours);
    setUseDefaultHours(useDefaultHoursObj);

    // Fetch cutoffs
    const cutoffsRes = await fetch(`${config.API_URL}/admin/stores/${storeId}/early-cutoffs`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const cutoffsData = await cutoffsRes.json();

    // Convert cutoffs to object
    const storeCutoffsObj: Record<number, string> = {};
    cutoffsData.forEach((c: any) => {
      storeCutoffsObj[c.dayOfWeek] = c.cutoffTime.substring(0, 5);
    });

    // Set default cutoffs from data.defaults
    const defaultCutoffsObj: Record<number, string> = {};
    if (data.defaults.earlyCutoffs) {
      data.defaults.earlyCutoffs.forEach((c: any) => {
        defaultCutoffsObj[c.dayOfWeek] = c.cutoffTime.substring(0, 5);
      });
    }

    // Set use default checkboxes
    const useDefaultCutoffsObj: Record<number, boolean> = {};
    const finalCutoffs: Record<number, string> = {};
    for (let day = 0; day < 7; day++) {
      if (storeCutoffsObj[day]) {
        useDefaultCutoffsObj[day] = false;
        finalCutoffs[day] = storeCutoffsObj[day];
      } else {
        useDefaultCutoffsObj[day] = true;
        finalCutoffs[day] = defaultCutoffsObj[day] || '';
      }
    }

    setDefaultCutoffs(defaultCutoffsObj);
    setEarlyCutoffs(finalCutoffs);
    setUseDefaultCutoffs(useDefaultCutoffsObj);

    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const token = await getValidToken();

    // Strip slaWebhookUrl from alertSettings (webhooks managed on Integrations page)
    const cleanAlertSettings = useDefaultAlertSettings ? null : {
      alertsEnabled: alertSettings.alertsEnabled,
      pickingSlaMinutes: alertSettings.pickingSlaMinutes,
      packingSlaMinutes: alertSettings.packingSlaMinutes,
      pickupSlaMinutes: alertSettings.pickupSlaMinutes,
    };

    const body = {
      isActive,
      deliveryTiers: useDefaultTiers
        ? null
        : storeTiers.map((t) => ({
            maxKm: parseFloat(String(t.maxKm)),
            deliveryCharge: parseFloat(String(t.deliveryCharge)) || 0,
            serviceFee: parseFloat(String(t.serviceFee)) || 0,
            minimumCartCheckout: parseFloat(String(t.minimumCartCheckout)) || 0,
            freeDeliveryAmount: t.freeDeliveryAmount != null && String(t.freeDeliveryAmount) !== ''
              ? parseFloat(String(t.freeDeliveryAmount))
              : null,
          })),
      // Uber delivery settings
      pickupNotes: pickupNotes.trim() === '' ? null : pickupNotes.trim(),
      uberDropoffVerification: useDefaultUberDropoff ? null : uberDropoffVerification,
      uberReturnVerification: useDefaultUberReturn ? null : uberReturnVerification,
      // Operations settings
      enablePickingFlow,
      alertSettings: cleanAlertSettings,
    };

    Logger.log('Saving store settings:', JSON.stringify(body, null, 2));


    const response = await fetch(`${config.API_URL}/admin/stores/${storeId}/settings`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    setSaving(false);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      Logger.error('Save failed:', response.status, errorData);
      alert(`Failed to save store settings: ${errorData.message || response.statusText}`);
      return;
    }

    alert('Store settings saved!');
  };

  // Uber settings handlers
  const handleToggleDefaultUberDropoff = (checked: boolean) => {
    setUseDefaultUberDropoff(checked);
    if (checked) {
      setUberDropoffVerification(defaultUberDropoff);
    }
  };

  const handleToggleDefaultUberReturn = (checked: boolean) => {
    setUseDefaultUberReturn(checked);
    if (checked) {
      setUberReturnVerification(defaultUberReturn);
    }
  };

  // Alert settings handlers
  const handleToggleDefaultAlertSettings = (checked: boolean) => {
    setUseDefaultAlertSettings(checked);
    if (checked) {
      setAlertSettings(defaultAlertSettings);
    }
  };

  const updateAlertSetting = (field: keyof AlertSettings, value: boolean | number | string | undefined) => {
    setAlertSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Update a nested field in Uber verification settings.
   * Handles both dropoff and return verification objects.
   */
  const updateUberVerification = (
    type: 'dropoff' | 'return',
    field: string,
    value: boolean | string
  ) => {
    const setter = type === 'dropoff' ? setUberDropoffVerification : setUberReturnVerification;
    const current = type === 'dropoff' ? uberDropoffVerification : uberReturnVerification;

    if (!current) return;

    if (field === 'verificationMethod') {
      setter({
        ...current,
        verificationMethod: value as string,
      } as any);
    } else if (field === 'signature.enabled') {
      setter({
        ...current,
        signature: { ...current.signature!, enabled: value as boolean },
      });
    } else if (field === 'signature.collect_signer_name') {
      setter({
        ...current,
        signature: { ...current.signature!, collect_signer_name: value as boolean },
      });
    } else if (field === 'signature.collect_signer_relationship') {
      setter({
        ...current,
        signature: { ...current.signature!, collect_signer_relationship: value as boolean },
      });
    } else if (field === 'picture') {
      setter({ ...current, picture: value as boolean });
    } else if (field === 'pincode.enabled') {
      setter({
        ...current,
        pincode: { ...current.pincode, enabled: value as boolean },
      });
    }
  };

  /**
   * Render a verification settings section (dropoff or return).
   */
  const renderVerificationSettings = (
    type: 'dropoff' | 'return',
    settings: UberVerificationSettings | null,
    useDefault: boolean,
    disabled: boolean
  ) => {
    if (!settings) return null;

    const isDropoff = type === 'dropoff';
    const label = isDropoff ? 'Customer Dropoff' : 'Store Return';
    const verificationMethod = (settings as any).verificationMethod || 'pincode';

    return (
      <Box sx={{ ml: 2, mt: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          {label} Verification
        </Typography>

        {isDropoff ? (
          <>
            {/* Uber API Constraint Alert */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Uber API Requirement:</strong> Must use EITHER Signature OR PIN (not both).
            </Alert>

            {/* Verification Method Toggle for Dropoff */}
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Verification Method:
              </Typography>
              <RadioGroup
                row
                value={verificationMethod}
                onChange={(e) => updateUberVerification(type, 'verificationMethod', e.target.value)}
              >
                <FormControlLabel
                  value="pincode"
                  control={<Radio disabled={disabled} />}
                  label="PIN (4-digit)"
                />
                <FormControlLabel
                  value="signature"
                  control={<Radio disabled={disabled} />}
                  label="Signature"
                />
              </RadioGroup>
            </FormControl>

            {/* Conditional rendering based on verification method */}
            {verificationMethod === 'signature' ? (
              <>
                {/* Signature Settings */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.signature?.enabled ?? false}
                      onChange={(e) => updateUberVerification(type, 'signature.enabled', e.target.checked)}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label="Require Signature"
                />
                {settings.signature?.enabled && (
                  <Box sx={{ ml: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.signature?.collect_signer_name ?? false}
                          onChange={(e) => updateUberVerification(type, 'signature.collect_signer_name', e.target.checked)}
                          disabled={disabled}
                          size="small"
                        />
                      }
                      label="Collect signer name"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={settings.signature?.collect_signer_relationship ?? false}
                          onChange={(e) => updateUberVerification(type, 'signature.collect_signer_relationship', e.target.checked)}
                          disabled={disabled}
                          size="small"
                        />
                      }
                      label="Collect relationship"
                    />
                  </Box>
                )}
              </>
            ) : (
              <>
                {/* PIN Settings (Default) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControlLabel
                    control={<Switch checked={true} disabled size="small" />}
                    label="Require PIN (4-digit)"
                  />
                  <Tooltip title="Customer provides 4-digit PIN to courier for delivery verification.">
                    <InfoIcon fontSize="small" color="action" />
                  </Tooltip>
                </Box>
              </>
            )}

            {/* Picture (optional, separate from signature/PIN) */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.picture ?? false}
                  onChange={(e) => updateUberVerification(type, 'picture', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Require Photo"
            />
          </>
        ) : (
          <>
            {/* Return Verification: Picture + PIN only (no signature) */}
            <Alert severity="info" sx={{ mb: 2 }}>
              Return verification uses <strong>Photo + PIN</strong> for proof of return to store.
            </Alert>

            {/* Picture */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.picture ?? false}
                  onChange={(e) => updateUberVerification(type, 'picture', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Require Photo"
            />

            {/* PIN */}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.pincode?.enabled ?? false}
                  onChange={(e) => updateUberVerification(type, 'pincode.enabled', e.target.checked)}
                  disabled={disabled}
                  size="small"
                />
              }
              label="Require PIN"
            />
          </>
        )}
      </Box>
    );
  };

  if (loading) return <CircularProgress />;

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5">Store Settings: {storeName}</Typography>
          <Button variant="outlined" onClick={() => navigate('/stores')}>
            ← Back to Stores
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Configure store-specific pricing or use system defaults. NULL values automatically use system defaults.
        </Alert>

        {/* Store Active Status */}
        <Box
          sx={{
            border: (theme) => `2px solid ${isActive ? theme.palette.success.main : theme.palette.error.main}`,
            borderRadius: 1,
            p: 2,
            mb: 2,
            bgcolor: (theme) => isActive
              ? `${theme.palette.success.main}15`
              : `${theme.palette.error.main}15`,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color={isActive ? 'success' : 'error'}
              />
            }
            label={
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">
                  {isActive ? '✓ Store is Active' : '✗ Store is Inactive'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isActive
                    ? 'This store is currently accepting online orders'
                    : 'This store is NOT accepting online orders'}
                </Typography>
              </Box>
            }
          />
        </Box>

        {/* Pricing & Cart Settings Section */}
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => setPricingSectionExpanded(!pricingSectionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCartIcon color="primary" />
              <Typography variant="h6">Pricing & Cart Settings</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {pricingSectionExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>

          <Collapse in={pricingSectionExpanded}>
            <Box sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useDefaultTiers}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setUseDefaultTiers(checked);
                      if (checked) {
                        setStoreTiers(defaultTiers);
                      }
                    }}
                  />
                }
                label="Use system default tiers"
              />

              {useDefaultTiers ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Using system default delivery tiers:
                  </Typography>
                  <DeliveryTierBuilder tiers={defaultTiers} onChange={() => {}} readOnly />
                </Box>
              ) : (
                <Box sx={{ mt: 2 }}>
                  <DeliveryTierBuilder tiers={storeTiers} onChange={setStoreTiers} />
                </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Working Hours Section */}
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => setHoursSectionExpanded(!hoursSectionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon color="primary" />
              <Typography variant="h6">Working Hours</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {hoursSectionExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>

          <Collapse in={hoursSectionExpanded}>
            <Box sx={{ p: 2 }}>
              {DAYS.map((day, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useDefaultHours[index] ?? true}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseDefaultHours({ ...useDefaultHours, [index]: checked });
                          if (checked && defaultHours[index]) {
                            setWorkingHours({ ...workingHours, [index]: defaultHours[index] });
                          }
                        }}
                      />
                    }
                    label={`Use system default for ${day}`}
                  />
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={6}>
                      <TextField
                        label="Open Time"
                        type="time"
                        value={workingHours[index]?.openTime || '08:00'}
                        onChange={(e) =>
                          setWorkingHours({
                            ...workingHours,
                            [index]: { ...workingHours[index], openTime: e.target.value },
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={useDefaultHours[index]}
                        size="small"
                      />
                    </Grid>
                    <Grid size={6}>
                      <TextField
                        label="Close Time"
                        type="time"
                        value={workingHours[index]?.closeTime || '22:00'}
                        onChange={(e) =>
                          setWorkingHours({
                            ...workingHours,
                            [index]: { ...workingHours[index], closeTime: e.target.value },
                          })
                        }
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        disabled={useDefaultHours[index]}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Liquor Cutoff Times Section */}
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => setCutoffsSectionExpanded(!cutoffsSectionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WineBarIcon color="primary" />
              <Typography variant="h6">Liquor Cutoff Times</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {cutoffsSectionExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>

          <Collapse in={cutoffsSectionExpanded}>
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Set times when alcohol sales stop. Leave blank for no cutoff.
              </Alert>
              {DAYS.map((day, index) => (
                <Box key={index} sx={{ mb: 2, p: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={useDefaultCutoffs[index] ?? true}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setUseDefaultCutoffs({ ...useDefaultCutoffs, [index]: checked });
                          if (checked) {
                            setEarlyCutoffs({ ...earlyCutoffs, [index]: defaultCutoffs[index] || '' });
                          }
                        }}
                      />
                    }
                    label={`Use system default for ${day}`}
                  />
                  <TextField
                    label={`${day} Cutoff Time (optional)`}
                    type="time"
                    value={earlyCutoffs[index] || ''}
                    onChange={(e) =>
                      setEarlyCutoffs({
                        ...earlyCutoffs,
                        [index]: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    disabled={useDefaultCutoffs[index]}
                    size="small"
                    helperText={
                      earlyCutoffs[index]
                        ? `Alcohol sales stop at ${earlyCutoffs[index]}`
                        : 'No liquor cutoff for this day'
                    }
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              ))}
            </Box>
          </Collapse>
        </Box>

        {/* Order Operations Settings Section */}
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => setOperationsSectionExpanded(!operationsSectionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InventoryIcon color="primary" />
              <Typography variant="h6">Order Operations</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {operationsSectionExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>

          <Collapse in={operationsSectionExpanded}>
            <Box sx={{ p: 2 }}>
              {/* Picking Flow Toggle */}
              <Box sx={{ mb: 3, p: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InventoryIcon fontSize="small" />
                      Enable Picking Flow
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      When enabled, orders will show a "Begin Picking" button before they can be packed.
                      This helps track who is actively picking an order and prevents duplicate work.
                    </Typography>
                  </Box>
                  <Switch
                    checked={enablePickingFlow}
                    onChange={(e) => setEnablePickingFlow(e.target.checked)}
                    color="primary"
                  />
                </Box>
                {enablePickingFlow && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Order Flow:</strong> PLACED → PICKING → PACKED → DISPATCHED → ...
                    <br />
                    Staff must click "Begin Picking" to claim an order before packing it.
                  </Alert>
                )}
              </Box>

              {/* Alert Settings (SLA config — hidden behind SHOW_SLA feature flag) */}
              {SHOW_SLA && (
              <Box sx={{ p: 2, border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <NotificationsActiveIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">
                    SLA Alert Settings
                  </Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={useDefaultAlertSettings}
                      onChange={(e) => handleToggleDefaultAlertSettings(e.target.checked)}
                    />
                  }
                  label="Use system default alert settings"
                />

                <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                  Alerts notify managers when orders sit too long in each state.
                  Times are in minutes from when the order enters that state.
                </Alert>

                {/* Alerts Enabled Toggle */}
                <Box sx={{ mb: 2, ml: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={alertSettings.alertsEnabled}
                        onChange={(e) => updateAlertSetting('alertsEnabled', e.target.checked)}
                        disabled={useDefaultAlertSettings}
                      />
                    }
                    label="Enable SLA Alerts for this Store"
                  />
                </Box>

                <Grid container spacing={2}>
                  {/* Picking SLA (PLACED state) */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Picking SLA (minutes)"
                      type="number"
                      value={alertSettings.pickingSlaMinutes}
                      onChange={(e) => updateAlertSetting('pickingSlaMinutes', parseInt(e.target.value) || 1)}
                      fullWidth
                      disabled={useDefaultAlertSettings || !alertSettings.alertsEnabled}
                      helperText={
                        useDefaultAlertSettings
                          ? `Default: ${defaultAlertSettings.pickingSlaMinutes} min`
                          : 'Time before alert if order waiting to start picking'
                      }
                      inputProps={{ min: 1 }}
                      size="small"
                    />
                  </Grid>

                  {/* Packing SLA (PICKING state) */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Packing SLA (minutes)"
                      type="number"
                      value={alertSettings.packingSlaMinutes}
                      onChange={(e) => updateAlertSetting('packingSlaMinutes', parseInt(e.target.value) || 1)}
                      fullWidth
                      disabled={useDefaultAlertSettings || !alertSettings.alertsEnabled}
                      helperText={
                        useDefaultAlertSettings
                          ? `Default: ${defaultAlertSettings.packingSlaMinutes} min`
                          : 'Time before alert if order in PICKING too long'
                      }
                      inputProps={{ min: 1 }}
                      size="small"
                    />
                  </Grid>

                  {/* Pickup SLA (PACKED state) */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      label="Pickup SLA (minutes)"
                      type="number"
                      value={alertSettings.pickupSlaMinutes}
                      onChange={(e) => updateAlertSetting('pickupSlaMinutes', parseInt(e.target.value) || 1)}
                      fullWidth
                      disabled={useDefaultAlertSettings || !alertSettings.alertsEnabled}
                      helperText={
                        useDefaultAlertSettings
                          ? `Default: ${defaultAlertSettings.pickupSlaMinutes} min`
                          : 'Time before alert if PACKED waiting for courier'
                      }
                      inputProps={{ min: 1 }}
                      size="small"
                    />
                  </Grid>
                </Grid>

                {/* Note: SLA Webhooks are configured on the Integrations page */}
              </Box>
              )}
            </Box>
          </Collapse>
        </Box>

        {/* Uber Delivery Settings Section */}
        <Box
          sx={{
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          {/* Section Header - Clickable to expand/collapse */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'action.hover',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.selected' },
            }}
            onClick={() => setUberSectionExpanded(!uberSectionExpanded)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShippingIcon color="primary" />
              <Typography variant="h6">Uber Delivery Settings</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              {uberSectionExpanded ? 'Click to collapse' : 'Click to expand'}
            </Typography>
          </Box>

          {/* Collapsible Content */}
          <Collapse in={uberSectionExpanded}>
            <Box sx={{ p: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Uber Direct verification settings applied to all deliveries from this store. These are not configurable — they reflect what Uber enforces.
              </Alert>

              {/* Pickup Notes - Store-specific instructions for courier at pickup */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  label="Pickup Notes for Courier"
                  value={pickupNotes}
                  onChange={(e) => setPickupNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="e.g., Ring doorbell twice, collect from rear entrance, etc."
                  helperText="Instructions for Uber courier when collecting from this store (max 500 chars)"
                  inputProps={{ maxLength: 500 }}
                />
              </Box>

              <Grid container spacing={3}>
                {/* Standard Delivery */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Standard Delivery
                    </Typography>
                    <FormControlLabel control={<Switch checked disabled size="small" />} label="PIN Code" />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4.5 }}>
                      Uber generates a 4-digit PIN sent to customer via SMS
                    </Typography>
                  </Box>
                </Grid>

                {/* Restricted Items */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Restricted Items
                    </Typography>
                    <FormControlLabel control={<Switch checked disabled size="small" />} label="PIN Code" />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4.5, mb: 1 }}>
                      4-digit PIN for delivery confirmation
                    </Typography>
                    <FormControlLabel control={<Switch checked disabled size="small" />} label="Age Verification (18+)" />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4.5 }}>
                      Courier verifies customer ID for alcohol/tobacco
                    </Typography>
                  </Box>
                </Grid>

                {/* Returns */}
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Failed Delivery Returns
                    </Typography>
                    <FormControlLabel control={<Switch checked disabled size="small" />} label="PIN Code" />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4.5, mb: 1 }}>
                      Store confirms return with PIN
                    </Typography>
                    <FormControlLabel control={<Switch checked disabled size="small" />} label="Photo Proof" />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4.5 }}>
                      Courier photographs returned package
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Note:</strong> PIN codes are generated by Uber (not configurable). Age verification is automatically
                added for restricted products. Return verification is sent but not officially documented by Uber.
              </Alert>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/stores')}>
            Cancel
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
